import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit, Layout, Trash2, Rocket } from 'lucide-react';
import { Tooltip } from '../../components/Tooltip';
import type { PageLayout } from '../../schemas/validation';
import type { PublishStatus } from '../../hooks/useUnpublishedSummary';

/** Display-friendly status */
type DisplayPublishStatus = 'draft' | 'unpublished_changes' | 'published';

/** Map backend canonical status to display-friendly values */
function toDisplayStatus(backend?: PublishStatus): DisplayPublishStatus {
  if (!backend || backend === 'never_published') return 'draft';
  if (backend === 'changed') return 'unpublished_changes';
  return 'published';
}

interface LayoutsSectionProps {
  layouts: PageLayout[];
  siteId: string;
  isLoading: boolean;
  formatDate: (date: string) => string;
  onDeleteClick: (layout: PageLayout) => void;
  onPublishLayout?: (layout: PageLayout) => void;
  publishingLayoutId?: string | null;
  /** Backend-computed status per layout (from unpublished-summary) */
  layoutStatusMap?: Record<string, PublishStatus>;
}

export const LayoutsSection: React.FC<LayoutsSectionProps> = ({
  layouts,
  siteId,
  isLoading,
  formatDate,
  onDeleteClick,
  onPublishLayout,
  publishingLayoutId,
  layoutStatusMap,
}) => {
  const navigate = useNavigate();

  return (
    <div className="mb-8">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center space-x-3">
          <Layout className="h-6 w-6 text-gray-400" />
          <h2 className="text-lg font-medium text-gray-900">Page Layouts</h2>
        </div>
        <button
          onClick={() => navigate(`/sites/${siteId}/layouts/new`)}
          className="btn-primary inline-flex items-center space-x-2"
        >
          <Plus size={20} />
          <span>Create Layout</span>
        </button>
      </div>

      {isLoading ? (
        <div className="text-center py-8 bg-white rounded-lg shadow">
          <div className="w-8 h-8 spinner mx-auto"></div>
          <p className="mt-2 text-sm text-gray-500">Loading layouts...</p>
        </div>
      ) : layouts.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <Layout className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No site layout yet</h3>
          <p className="mt-1 text-sm text-gray-500 max-w-md mx-auto">
            A layout adds a shared navigation bar and footer to every page on your site. Start here
            before building pages.
          </p>
          <div className="mt-6">
            <button
              onClick={() => navigate(`/sites/${siteId}/layouts/new`)}
              className="btn-primary inline-flex items-center space-x-2"
            >
              <Plus size={20} />
              <span>Create Layout</span>
            </button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {layouts.map(layout => (
            <div key={layout.id} className="card p-6">
              <div className="flex items-start justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-purple-500 to-purple-600 flex items-center justify-center shadow-sm">
                    <Layout className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <div className="flex items-center space-x-2 flex-wrap gap-y-1">
                      <h3 className="text-lg font-semibold text-gray-900">{layout.name}</h3>
                      {layout.isDefault && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                          Default
                        </span>
                      )}
                      {/* Publish Status Badge (from backend summary) */}
                      {(() => {
                        const status = toDisplayStatus(layoutStatusMap?.[layout.id]);
                        if (status === 'draft')
                          return (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-yellow-100 text-yellow-800">
                              Draft
                            </span>
                          );
                        if (status === 'unpublished_changes')
                          return (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-orange-100 text-orange-800">
                              Unpublished changes
                            </span>
                          );
                        return (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-green-100 text-green-800">
                            Published
                          </span>
                        );
                      })()}
                    </div>
                    {layout.description && (
                      <p className="text-sm text-gray-500 line-clamp-1">{layout.description}</p>
                    )}
                  </div>
                </div>
              </div>

              <div className="mt-4 flex items-center justify-between text-sm text-gray-500">
                <span>{layout.components?.length || 0} components</span>
                <span>Updated {formatDate(layout.updatedAt)}</span>
              </div>

              <div className="mt-4 flex items-center space-x-2">
                <button
                  onClick={() => navigate(`/sites/${siteId}/layouts/${layout.id}`)}
                  className="flex-1 btn-primary inline-flex items-center justify-center space-x-2"
                >
                  <Edit size={16} />
                  <span>Edit Layout</span>
                </button>
                {/* Quick Publish — show when layout has unpublished changes */}
                {onPublishLayout &&
                  toDisplayStatus(layoutStatusMap?.[layout.id]) !== 'published' && (
                    <Tooltip
                      content={
                        toDisplayStatus(layoutStatusMap?.[layout.id]) === 'draft'
                          ? 'Publish layout'
                          : 'Republish layout'
                      }
                    >
                      <button
                        onClick={() => onPublishLayout(layout)}
                        disabled={publishingLayoutId === layout.id}
                        className="p-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50"
                      >
                        {publishingLayoutId === layout.id ? (
                          <div className="w-[18px] h-[18px] border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <Rocket size={18} />
                        )}
                      </button>
                    </Tooltip>
                  )}
                <Tooltip content="Delete Layout">
                  <button
                    onClick={() => onDeleteClick(layout)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <Trash2 size={18} />
                  </button>
                </Tooltip>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
